import { LotStatus } from "@/enums/LotStatus";

export interface MasterplanLotStatus {
  label: string;
  cssClass: string;
  bgColor: string;
  textColor: string;
}

export const MASTERPLAN_STATUS_COLORS: Record<
  string,
  { bg: string; text: string }
> = {
  success: { bg: "#059166", text: "#fff" },
  yellow: { bg: "#ed990b", text: "#fff" },
  "light-blue": { bg: "#60a5fa", text: "#fff" },
  primary: { bg: "#bc3535", text: "#fff" },
  warning: { bg: "#bc3535", text: "#fff" },
  orange: { bg: "#f97316", text: "#fff" },
  blue: { bg: "#3b82f6", text: "#fff" },
  gray: { bg: "#6b7280", text: "#fff" },
  "gray-middle": { bg: "#9ca3af", text: "#fff" },
};

export function getMasterplanCssVars(): Record<string, string> {
  const vars: Record<string, string> = {};

  for (const [cls, colors] of Object.entries(MASTERPLAN_STATUS_COLORS)) {
    vars[`--mp-${cls}-bg`] = colors.bg;
    vars[`--mp-${cls}-text`] = colors.text;
  }

  return vars;
}

export function getMasterplanStatusBadgeStyle(
  status: string | undefined | null
): Record<string, string> {
  const cssClass = getMasterplanLotStatusClass(status);
  const colors = MASTERPLAN_STATUS_COLORS[cssClass];

  if (!colors) return {};

  return { background: colors.bg, color: colors.text };
}

export function getMasterplanLotStatusLabel(
  status: string | undefined | null
): string {
  switch (status as LotStatus) {
    case LotStatus.SETTLED:
    case LotStatus.UNCONDITIONAL:
      return "Sold";
    case LotStatus.UNDER_CONTRACT:
    case LotStatus.CONTRACT_EXECUTED:
    case LotStatus.EOI:
      return "Reserved";
    default:
      return status || LotStatus.NOT_RELEASED;
  }
}

export function getMasterplanLotStatusClass(
  status: string | undefined | null
): string {
  if (!status) return "gray";

  switch (status as LotStatus) {
    case LotStatus.NOT_RELEASED:
      return "gray";
    case LotStatus.AVAILABLE:
      return "success";
    case LotStatus.HOLD:
      return "light-blue";
    case LotStatus.EOI:
    case LotStatus.CONTRACT_EXECUTED:
    case LotStatus.UNDER_CONTRACT:
      return "yellow";
    case LotStatus.UNCONDITIONAL:
      return "primary";
    case LotStatus.SETTLED:
      return "warning";
    case LotStatus.OPTION:
      return "orange";
    case LotStatus.OFFLINE:
      return "gray-middle";
    case LotStatus.PRE_CONTRACT:
      return "blue";
    default:
      return "";
  }
}

export function getMasterplanLotStatus(
  status: string | undefined | null
): MasterplanLotStatus {
  const label = getMasterplanLotStatusLabel(status);
  const cssClass = getMasterplanLotStatusClass(status);
  const colors = MASTERPLAN_STATUS_COLORS[cssClass] ?? {
    bg: "rgba(255,255,255,0.15)",
    text: "#fff",
  };

  return { label, cssClass, bgColor: colors.bg, textColor: colors.text };
}
